<template>
  <v-container fluid>
    <v-progress-linear
      indeterminate
      color="yellow darken-2"
      v-show="loading"
    ></v-progress-linear>
    <v-card color="white">
      <v-card-title primary-title class="bg-clr">
        <span class="headline">Credit Limit Report</span>
        <v-spacer />
        <span class="muted caption" v-if="run">
          <v-chip small color="orange" dark v-if="run.dryRun">DRY RUN</v-chip>
          Run {{ run.runDate }} · {{ run.status }} ·
          {{ formatDateTime(run.startedAt) }} to
          {{ formatDateTime(run.completedAt) }}
        </span>
      </v-card-title>

      <v-card-text class="content-border">
        <v-layout wrap align-center>
          <v-flex xs12 md2 pr-3>
            <v-menu
              ref="dateMenu"
              lazy
              v-model="dateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              full-width
              min-width="290px"
              :return-value.sync="date"
            >
              <v-text-field
                slot="activator"
                label="Run date (latest if empty)"
                v-model="date"
                prepend-icon="event"
                readonly
                clearable
                @click:clear="
                  date = '';
                  reload();
                "
              ></v-text-field>
              <v-date-picker
                v-model="date"
                :max="today"
                no-title
                scrollable
                @change="
                  $refs.dateMenu.save(date);
                  reload();
                "
              ></v-date-picker>
            </v-menu>
          </v-flex>
          <v-flex xs12 md3 pr-3>
            <v-select
              v-model="action"
              :items="actionOptions"
              label="Action"
              @change="reload()"
            ></v-select>
          </v-flex>
          <v-flex xs12 md3 pr-3>
            <v-text-field
              v-model="search"
              label="Search serial, name or company"
              prepend-icon="search"
              clearable
              @keyup.enter="reload()"
              @click:clear="
                search = '';
                reload();
              "
            ></v-text-field>
          </v-flex>
          <v-flex xs12 md4 class="text-xs-right">
            <v-btn
              flat
              color="orange darken-1"
              :disabled="loading"
              @click="runNow()"
            >
              Run now
              <v-icon right>play_arrow</v-icon>
            </v-btn>
            <v-btn
              flat
              :disabled="loading"
              @click="getReport()"
            >
              Refresh
              <v-icon right>refresh</v-icon>
            </v-btn>
            <v-btn
              flat
              color="orange darken-1"
              :disabled="loading || !run"
              @click="downloadCsv()"
            >
              Download CSV
              <v-icon right>file_download</v-icon>
            </v-btn>
          </v-flex>
        </v-layout>

        <v-layout wrap v-if="run" class="pb-3">
          <v-flex
            xs6
            md2
            pr-3
            v-for="card in summaryCards"
            :key="card.label"
          >
            <div class="muted caption">{{ card.label }}</div>
            <div class="title" :class="card.color">{{ card.value }}</div>
          </v-flex>
        </v-layout>

        <v-alert :value="!loading && !run" color="blue" icon="info">
          No credit limit cron run saved{{ date ? ` for ${date}` : "" }}.
        </v-alert>

        <v-data-table
          v-if="run"
          hide-actions
          :headers="headers"
          :items="items"
        >
          <template slot="items" slot-scope="props">
            <td>
              <div>{{ props.item.customerSerialNumber || "-" }}</div>
              <div class="muted caption">
                {{ props.item.companyName || props.item.customerName }}
              </div>
            </td>
            <td>{{ props.item.workingCountry || "-" }}</td>
            <td>{{ amount(props.item.creditLimit, props.item) }}</td>
            <td>{{ amount(props.item.realbooksCurBalance, props.item) }}</td>
            <td>
              {{ amount(props.item.uninvoicedAmount, props.item) }}
              <div
                class="muted caption"
                v-if="props.item.uninvoicedJobsCount != null"
              >
                {{ props.item.uninvoicedJobsCount }} jobs
              </div>
            </td>
            <td>
              <b>{{ amount(props.item.totalExposure, props.item) }}</b>
            </td>
            <td>
              {{
                props.item.utilization != null
                  ? `${props.item.utilization}%`
                  : "-"
              }}
            </td>
            <td>{{ props.item.wasBlocked ? "Blocked" : "Open" }}</td>
            <td>
              <v-chip small :color="actionColor(props.item.action)" dark>
                {{ props.item.action }}
              </v-chip>
            </td>
            <td class="caption">{{ props.item.reason || "-" }}</td>
          </template>
        </v-data-table>

        <v-layout v-if="run && totalCount > limit" align-center pt-3>
          <span class="muted caption">
            {{ skip + 1 }}-{{ Math.min(skip + limit, totalCount) }} of
            {{ totalCount }}
          </span>
          <v-spacer />
          <v-btn
            flat
            :disabled="loading || skip === 0"
            @click="changePage(-1)"
          >
            Previous
          </v-btn>
          <v-btn
            flat
            :disabled="loading || skip + limit >= totalCount"
            @click="changePage(1)"
          >
            Next
          </v-btn>
        </v-layout>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import moment from "moment";
import Swal from "sweetalert2";
import { StorageKeys } from "../../constants/constants";
import {
  creditLimitCronReport,
  runCreditLimitCron,
} from "../../constants/api-urls";

const CSV_PAGE_SIZE = 1000;

export default {
  data() {
    return {
      loading: false,
      dateMenu: false,
      date: "",
      today: moment().format("YYYY-MM-DD"),
      action: "BLOCKED,UNBLOCKED",
      actionOptions: [
        { text: "Blocked and unblocked", value: "BLOCKED,UNBLOCKED" },
        { text: "Blocked", value: "BLOCKED" },
        { text: "Unblocked", value: "UNBLOCKED" },
        { text: "Unchanged", value: "UNCHANGED" },
        { text: "Skipped (RealBooks / error)", value: "SKIPPED" },
        { text: "All customers", value: "" },
      ],
      search: "",
      skip: 0,
      limit: 50,
      run: null,
      actionCounts: {},
      totalCount: 0,
      items: [],
      headers: [
        { text: "Customer", sortable: false, class: "table-header-item" },
        { text: "Country", sortable: false, class: "table-header-item" },
        { text: "Credit Limit", sortable: false, class: "table-header-item" },
        {
          text: "RealBooks Balance",
          sortable: false,
          class: "table-header-item",
        },
        { text: "Uninvoiced", sortable: false, class: "table-header-item" },
        { text: "Exposure", sortable: false, class: "table-header-item" },
        { text: "Used %", sortable: false, class: "table-header-item" },
        { text: "Before", sortable: false, class: "table-header-item" },
        { text: "Action", sortable: false, class: "table-header-item" },
        { text: "Reason", sortable: false, class: "table-header-item" },
      ],
    };
  },
  computed: {
    summaryCards() {
      const counts = this.actionCounts;
      return [
        { label: "Customers checked", value: this.run.totalCustomers || 0 },
        { label: "Blocked", value: counts.BLOCKED || 0, color: "red--text" },
        {
          label: "Unblocked",
          value: counts.UNBLOCKED || 0,
          color: "green--text",
        },
        { label: "Unchanged", value: counts.UNCHANGED || 0 },
        {
          label: "Skipped",
          value: counts.SKIPPED || 0,
          color: "orange--text",
        },
        {
          label: "Duration",
          value:
            this.run.durationSeconds != null
              ? `${this.run.durationSeconds}s`
              : "-",
        },
      ];
    },
  },
  created() {
    this.reload();
  },
  methods: {
    authConfig() {
      delete this.axios.defaults.headers.common["token"];
      return {
        headers: {
          authorization: `bearer ${localStorage.getItem(
            StorageKeys.SessionKey
          )}`,
        },
      };
    },
    // Dry run saves the report only; a real run blocks and unblocks like 02:00.
    runNow() {
      Swal.fire({
        title: "Run credit limit check now?",
        text: "Dry run only saves the report. Real run also blocks and unblocks customers.",
        type: "question",
        showCancelButton: true,
        confirmButtonColor: "orange",
        confirmButtonText: "Dry run",
        cancelButtonText: "Real run",
        showCloseButton: true,
      }).then((result) => {
        if (result.value) {
          this.startRun(true);
        } else if (result.dismiss === Swal.DismissReason.cancel) {
          Swal.fire({
            title: "Block and unblock customers now?",
            text: "This changes customers exactly like the 02:00 cron.",
            type: "warning",
            showCancelButton: true,
            confirmButtonColor: "red",
            confirmButtonText: "Yes, real run",
          }).then((confirm) => {
            if (confirm.value) this.startRun(false);
          });
        }
      });
    },
    startRun(dryRun) {
      this.loading = true;
      this.axios
        .post(
          `${this.constants.apiUrl}${runCreditLimitCron}`,
          { dryRun },
          this.authConfig()
        )
        .then((res) => {
          if (res.data.statusCode !== 200) {
            return Promise.reject(res.data);
          }
          const data = res.data.data || {};
          Swal.fire({
            title: data.started ? "Run started" : "Not started",
            text: `${data.message}. Press Refresh to see the report.`,
            type: data.started ? "success" : "info",
            confirmButtonColor: "orange",
          });
          this.date = "";
          this.reload();
        })
        .catch((err) => this.showError(err))
        .finally(() => {
          this.loading = false;
        });
    },
    query(skip, limit) {
      const params = { skip, limit };
      if (this.date) params.date = this.date;
      if (this.action) params.action = this.action;
      if (this.search) params.search = this.search.trim();
      return Object.keys(params)
        .map((key) => `${key}=${encodeURIComponent(params[key])}`)
        .join("&");
    },
    fetchPage(skip, limit) {
      return this.axios
        .get(
          `${this.constants.apiUrl}${creditLimitCronReport}?${this.query(
            skip,
            limit
          )}`,
          this.authConfig()
        )
        .then((res) => {
          if (res.data.statusCode !== 200) {
            return Promise.reject(res.data);
          }
          return res.data.data;
        });
    },
    reload() {
      this.skip = 0;
      this.getReport();
    },
    changePage(direction) {
      this.skip = Math.max(this.skip + direction * this.limit, 0);
      this.getReport();
    },
    getReport() {
      this.loading = true;
      this.fetchPage(this.skip, this.limit)
        .then((data) => {
          this.run = data.run;
          this.actionCounts = data.actionCounts || {};
          this.totalCount = data.totalCount || 0;
          this.items = data.customers || [];
        })
        .catch((err) => this.showError(err))
        .finally(() => {
          this.loading = false;
        });
    },
    async downloadCsv() {
      this.loading = true;
      try {
        let rows = [];
        let total = 0;
        do {
          const data = await this.fetchPage(rows.length, CSV_PAGE_SIZE);
          total = data.totalCount || 0;
          rows = rows.concat(data.customers || []);
          if (!(data.customers || []).length) break;
        } while (rows.length < total);

        const columns = [
          ["Run Date", (r) => r.runDate],
          ["Customer Serial", (r) => r.customerSerialNumber],
          ["Customer Name", (r) => r.customerName],
          ["Company Name", (r) => r.companyName],
          ["Country", (r) => r.workingCountry],
          ["Currency", (r) => r.currency],
          ["Ledger", (r) => r.ledger],
          ["Credit Limit", (r) => r.creditLimit],
          ["RealBooks Current Balance", (r) => r.realbooksCurBalance],
          ["Amount Outstanding", (r) => r.amountOutstanding],
          ["Uninvoiced Jobs", (r) => r.uninvoicedJobsCount],
          ["Uninvoiced Job Price", (r) => r.uninvoicedJobAmount],
          ["Uninvoiced Job VAT", (r) => r.uninvoicedJobVat],
          ["Additional Charges", (r) => r.uninvoicedAdditionalCharges],
          ["Additional Charges VAT", (r) => r.uninvoicedAdditionalChargesVat],
          ["Uninvoiced Amount", (r) => r.uninvoicedAmount],
          ["Total Exposure", (r) => r.totalExposure],
          ["Used %", (r) => r.utilization],
          ["Blocked Before", (r) => (r.wasBlocked ? "Yes" : "No")],
          ["Action", (r) => r.action],
          ["Blocked After", (r) => (r.isBlockedAfter ? "Yes" : "No")],
          ["Reason", (r) => r.reason],
        ];
        const cell = (value) =>
          `"${value == null ? "" : String(value).replace(/"/g, '""')}"`;
        const csv = [columns.map(([title]) => cell(title)).join(",")]
          .concat(
            rows.map((row) => columns.map(([, get]) => cell(get(row))).join(","))
          )
          .join("\n");

        const link = document.createElement("a");
        link.href = URL.createObjectURL(
          new Blob([`﻿${csv}`], { type: "text/csv;charset=utf-8" })
        );
        link.download = `credit-limit-report-${
          this.run ? this.run.runDate : "latest"
        }.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
      } catch (err) {
        this.showError(err);
      } finally {
        this.loading = false;
      }
    },
    showError(err) {
      const data = (err && err.response && err.response.data) || err || {};
      Swal.fire({
        title: "Oops!",
        text: data.message || "Could not load the credit limit report",
        type: "error",
        confirmButtonColor: "orange",
        confirmButtonText: "Okay",
      });
    },
    amount(value, row) {
      if (value == null) return "-";
      const formatted = Number(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return row.currency ? `${formatted} ${row.currency}` : formatted;
    },
    actionColor(action) {
      return (
        {
          BLOCKED: "red",
          UNBLOCKED: "green",
          SKIPPED: "orange",
        }[action] || "grey"
      );
    },
    formatDateTime(value) {
      return value ? moment(value).format("DD/MM/YYYY hh:mm A") : "-";
    },
  },
};
</script>
